
import { ItemCompact } from "@/models/ItemCompact";
import ListRequest, { Filter, Ordering, OrderType } from "@/models/ListRequest";
import { FilterRecipesNames } from "@/models/Recipes/FilterRecipesNames";
import { Recipe } from "@/models/Recipes/Recipe";
import { AddOnType } from "@/models/Subscriptions/AddOns/AddOnType";
import { TRANSLATIONS_FETCH } from "@/store/ActionTypes";
import { getAllergenStatusMeta } from "@/utils/allergenStatus";
import RecipesRepository from "@/utils/API/RecipesRepository";
import { getBoldString } from "@/utils/Helpers";
import { Vue, Component, Prop } from "vue-property-decorator";
import { Snotify } from "vue-snotify";
import { mapState } from "vuex";
import VAllergenStatusIcon from "@/components/allergens/VAllergenStatusIcon.vue";
import vSelectFx from "@/components/vSelectFx.vue";
import vPreLoader from "./vPreLoader.vue";

interface RecipesState {
  // Grid properties
  count: number;
  totalCount: number;
  page: number;
  pageSize: number;
  orders: Ordering;
  filters: Filter;
  loading: boolean;

  // List of displayed recipes
  recipes: ItemCompact[];
  // Selected recipe
  selectedRecipeId: number;
  // Recipe search data (recipes by category)
  categories: FilterRecipesNames[];
}

@Component({
  components: {
    VAllergenStatusIcon,
    vPreLoader,
    vSelectFx,
  },
  computed: {
    ...mapState({
      localizationState: (state: any) => state.LocalizationModule,
      accountState: (state: any) => state.AccountModule,
    }),
  },
})

export default class vRecipeListing extends Vue {
  @Prop() isRecipe : boolean;

  localizationState: any;
  accountState: any;
  checked: number[] = [];

  recipesState: RecipesState = {
    count: 0,
    totalCount: 0,
    page: 1,
    pageSize: 10,
    orders: { field: "Name", orderType: OrderType.Asc },
    filters: { name: "Name", value: "" },
    loading: true,

    recipes: [],
    selectedRecipeId: 0,
    categories: [],
  };

  get translations() {
    return this.localizationState.translations;
  }

  get isAllergenAiAddonActive() {
    const addOns = this.accountState?.userDetails?.addOns || this.$store.getters.getUserDetails?.addOns || [];
    return addOns.some((addOn: any) => {
      const addOnType = typeof addOn === "object" ? addOn.addOn : addOn;
      return addOn?.isActive !== false && (addOnType === AddOnType.AllergenAI || addOnType === "AllergenAI");
    });
  }

  get recipeListColumnCount() {
    return this.isAllergenAiAddonActive ? 5 : 4;
  }

  getAllergenStatusVariant(recipe: ItemCompact) {
    return getAllergenStatusMeta(recipe).variant;
  }

  getAllergenStatusTitle(recipe: ItemCompact) {
    return getAllergenStatusMeta(recipe).title;
  }

  /**
   * Helper method for enabling / disabling remove button
   *
   * @return {boolean}
   */
  get isRemoveDisabled() {
    return this.checked.length === 0;
  }

  /**
   * Centralized method for fetching recipes and storing to state
   */
  requestRecipes(listRequest: ListRequest) {
    //this.recipesState.loading = true;
    RecipesRepository.listReducedRecipes(listRequest, this.isRecipe)
      .then((response) => {
        this.recipesState.recipes = response.data.result;
        // this.recipesState.recipes.map((recipe: any) => {
        //   recipe.nutritionAnalysis = mapNutritions(recipe.nutritionAnalysis);
        // });
        this.recipesState.count = response.data.count;
        this.recipesState.totalCount = response.data.totalCount;
        this.recipesState.loading = false;
      })
      .catch((error) => {
        if (error.response) {
          this.$snotify.error(
            error.response.data.errorMessages[0].userVisibleMessage
          );
        }
        else this.$snotify.error(this.translations.get("network-error"));
      });
  }

  /**
   * Method which handles ordering of the list of recipes (A-Z / Z-A)
   */
  orderPageSize(value: any) {
    this.recipesState.pageSize = value;

    if (value === "0") {
      this.recipesState.pageSize = 10;
    }
    else if (value === "1") {
      this.recipesState.pageSize = 20;
    }
    else if (value === "2") {
      this.recipesState.pageSize = 50;
    }
    else if (value === "3") {
      this.recipesState.pageSize = 100;
    }

    this.requestRecipes(
      new ListRequest({
        page: this.recipesState.page,
        pageSize: this.recipesState.pageSize,
        orders: [this.recipesState.orders],
        filters: [this.recipesState.filters],
      })
    );
  }

  /**
   * Method which handles ordering of the list of recipes (A-Z / Z-A)
   */
  order(value: OrderType) {
    this.recipesState.orders.orderType = value;

    this.requestRecipes(
      new ListRequest({
        page: this.recipesState.page,
        pageSize: this.recipesState.pageSize,
        orders: [this.recipesState.orders],
        filters: [this.recipesState.filters],
      })
    );
  }

  /**
   * Method for handling user click on Next on the recipes listing.
   * Requests the next page and stores data into state
   */
  next() {
    if (!this.disabledNext) {
      this.recipesState.page = this.recipesState.page + 1;

      this.requestRecipes(
        new ListRequest({
          page: this.recipesState.page,
          pageSize: this.recipesState.pageSize,
          orders: [this.recipesState.orders],
        })
      );
    }
  }

  /**
   * Method for handling user click on Prev on the recipes listing.
   * Requests the previous page and stores data into state
   */
  prev() {
    if (this.recipesState.page > 1) {
      this.recipesState.page = this.recipesState.page - 1;

      this.requestRecipes(
        new ListRequest({
          page: this.recipesState.page,
          pageSize: this.recipesState.pageSize,
          orders: [this.recipesState.orders],
        })
      );
    }
  }

  /**
   * Disabling next button if there isn't next page
   *
   * @return {boolean}
   */
  get disabledNext() {
    return (
      Math.ceil(this.recipesState.totalCount / this.recipesState.pageSize) ===
      this.recipesState.page
    );
  }


  /**
   * Method which returns false if there is only one page of the recipe list
   * If true, Prev and Next buttons are shown
   *
   * @return {boolean}
   */
  get showControls() {
    return this.recipesState.totalCount > this.recipesState.count;
  }

  onAuditLogClick(recipe: ItemCompact) {
    this.$emit("onAuditLogClick", recipe);
  }

  onDeleteRecipeClick(){
    this.$emit("onDeleteRecipeClick", this.checked, this.removeRecipes);
  }

  /**
   * Hides all modals and resets some state variables
   */
  resetState() {
    this.recipesState.selectedRecipeId = 0;
    this.checked.length = 0;
    this.checked = [];
  }

  chooseRecipe(recipeId: number) {
    this.$emit("chooseRecipe", recipeId);
  }

  /**
   * Method which requests delete recipes to API and shows toastr message
   */
  removeRecipes(checked: number[]) {
    this.$snotify.async(
      this.translations.get("rec_toastr_deleting"),
      () => <Promise<Snotify>>new Promise((resolve, reject) => {
        RecipesRepository.deleteRecipes({ recipeIds: checked })
          .then(() => {
            this.recipesState.page = 1;
            this.requestRecipes(
              new ListRequest({
                page: this.recipesState.page,
                pageSize: this.recipesState.pageSize,
                orders: [this.recipesState.orders],
              })
            );
            this.resetState();
            $("#searchRecipes").val("").trigger("change");
            resolve({
              body: this.isRecipe ? this.translations.get("rec_toastr_deleted") : this.translations.get("rec_copy_combined_toastr_deleted"),
            });
          })
          .catch((error) => {
            let message = "";
            if (error.response) {
              message =
                error.response.data.errorMessages[0].userVisibleMessage;
            }
            else {
              message = this.translations.get("network_error");
            }
            reject({ body: message });
          });
      }),
      {
        timeout: 3000,
        closeOnClick: true,
      }
    );
  }

  created() {
    this.$store
      .dispatch(TRANSLATIONS_FETCH, {
        category: "pages",
        collections: ["common", "recipes", "dishes", "ingredients"],
      })
      .then(() => {
        document.title =
          this.translations.get("rec_title") +
          " " +
          this.translations.get("meta_title_postfix");
      })
      .catch((error) => {
        if (error.response) {
          this.$snotify.error(
            error.response.data.errorMessages[0].userVisibleMessage
          );
        }
        else this.$snotify.error(this.translations.get("network-error"));
      });

    this.requestRecipes(
      new ListRequest({
        page: this.recipesState.page,
        pageSize: this.recipesState.pageSize,
        orders: [this.recipesState.orders],
      })
    );
  }


  mounted() {
    (<any>window).$.Pages.init();
    this.initSearch();
  }

  /**
   * Initialization and event handling of recipe search (https://select2.org/)
   */
  initSearch() {
    // reset the select2 DOM node before reinit
    if ((<any>$("#searchRecipes")).hasClass("select2-hidden-accessible")) {
      (<any>$("#searchRecipes")).html("").select2();
    };
    let searchTerm = "";

    const restoreSearchTerm = () => {
      this.$nextTick(() => {
        const select2 = $("#searchRecipes").data("select2");
        if (!select2 || !select2.dropdown || !select2.dropdown.$search) return;

        const $search = select2.dropdown.$search;

        $search
          .val(searchTerm)
          .trigger("input")
          .off("input.fullreset")
          .on("input.fullreset", () => {
            searchTerm = $search.val();

            // If cleared, reset to full list
            if (!searchTerm) {
              this.resetRecipeFilters();
            }
          });
      });
    };

    (<any>$("#searchRecipes"))
      .select2({
        allowClear: false,
        placeholder: this.isRecipe ? "Search Recipe" : "Search Combined Recipe",
        minimumInputLength: 3,
        ajax: {
          delay: 500,
          // method that handles user input and does the ajax call
          transport: (params: any, success: any) => {
            if(this.isRecipe){
              RecipesRepository.filterRecipesNames(
                params.data.term ? params.data.term : ""
              ).then((response) => {
                success(response);
              });
            }
            else{
              RecipesRepository.filterCombinedRecipeName(
                params.data.term ? params.data.term : ""
              ).then((response) => {
                success(response);
              });
            }
            searchTerm = params.data.term;
          },
          // on success
          processResults: (response: any) => {
            const data = this.isRecipe
              ? this.processRecipeSearchResults(searchTerm, response)
              : this.processCombinedRecipeSearchResults(searchTerm, response);

            return { results: data };
          },
        },
        escapeMarkup: function(element:string){
          return element;
        },
      })
      .on("select2:select", (eventData: any) => {
        let value: string = <string>eventData.target.value;

        this.recipesState.page = 1;
        this.recipesState.orders = { field: "", orderType: OrderType.Asc };

        // check if category is selected
        if (value.startsWith("cat")) {
          let parts = value.split("-");
          // get the category id
          let categoryId = parts[1];
          // if id is not 0 then it means a specific category is selected and recipes will get filtered by selected category id
          if (categoryId !== "0") {
            this.recipesState.filters = {
              name: "RecipeCategoryId",
              value: categoryId,
            };
          }
          else {
            // if id is 0 it means the user selected All Recipes and we don't want to filter the results
            this.recipesState.filters = {
              name: "",
              value: "0",
            };
          }
        }
        else {
          if(value == "0"){
            this.recipesState.filters = { name: "", value: value };
          }
          else{
            this.recipesState.filters = { name: "Id", value: value };
          }
        }
        // we do the filtering here no matter what value the filter got up there :)
        this.requestRecipes(
          new ListRequest({
            page: this.recipesState.page,
            pageSize: this.recipesState.pageSize,
            filters: [this.recipesState.filters],
            orders: [this.recipesState.orders],
          })
        );

      })

      //restore term when dropdown reopens
      .on("select2:open", () => {
        restoreSearchTerm();
      });
  }

  resetRecipeFilters() {
    this.recipesState.page = 1;
    this.recipesState.filters = { name: "", value: "" };

    this.requestRecipes(
      new ListRequest({
        page: this.recipesState.page,
        pageSize: this.recipesState.pageSize,
        orders: [this.recipesState.orders],
      })
    );
  }

  processRecipeSearchResults(searchTerm: string, response: any){
    let data: any[] = [];
    data.push({ id: "cat-0", text: getBoldString("All Recipes", "") });

    // Grouping by category
    response.data.result.map((c: FilterRecipesNames) => {
      let children: any = [];

      // For each category
      if (c.recipes && c.recipes.length) {
        data.push({ id: `cat-${c.id}`, text: getBoldString(c.name, "") });

        // Put recipes as children inside
        c.recipes.map((recipe: Recipe) => {
          children.push({ id: recipe.id, text: getBoldString(recipe.name, searchTerm) });
        });
      }
      data.push({ children: children });
    });

    return data;
  }

  processCombinedRecipeSearchResults(searchTerm: string, response: any){
    let data: any[] = [];

    data.push({
      id: 0,
      text: this.translations.get("dish_all_dishes"),
    });
    response.data.result.map((r: any) => {
      data.push({ id: r.id, text: getBoldString(r.name, searchTerm) });
    });

    return data;
  }

}
